import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import {
  Check,
  HelpCircle,
  ImageIcon,
  Images,
  Loader2,
  Sparkles,
  Trash2,
  Upload,
  Users,
} from "lucide-react";
import { Link, Navigate } from "react-router";
import { toast } from "sonner";

import { trpc } from "@/lib/trpc";
import { Badge } from "@runcheetah/ui";
import { Button } from "@runcheetah/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@runcheetah/ui";
import {
  CenteredLoader,
  EmptyCard,
  ExternalLinkText,
  PageIntro,
  SegmentedFilter,
  Skeleton,
} from "@/components/common";
import { buildBrandPath, getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import {
  formatPeopleDetectionConfidence,
  getMediaGallerySourceLinkLabel,
  getRealPeopleReferencePolicyLabel,
  isMediaGalleryUploadProcessing,
  resolveMediaGalleryRealPeopleReferencePolicyApplies,
} from "@/lib/media-gallery";
import { cn } from "@/lib/utils";

type SourceFilter = "all" | "upload" | "generated" | "website" | "instagram";

type MediaUploadContentType = "image/png" | "image/jpeg" | "image/webp" | "image/svg+xml";

const MEDIA_UPLOAD_CONTENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
] as const satisfies readonly MediaUploadContentType[];
const MEDIA_UPLOAD_ACCEPT = MEDIA_UPLOAD_CONTENT_TYPES.join(",");
const MAX_MEDIA_UPLOAD_BYTES = 10 * 1024 * 1024;

const formatBytes = (bytes: number | null, intl: IntlShape): string => {
  if (bytes === null) {
    return intl.formatMessage({ id: "mediaGallery.unknownSize", defaultMessage: "Unknown size" });
  }
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatClassification = (value: string | null, intl: IntlShape): string => {
  if (!value) {
    return intl.formatMessage({
      id: "mediaGallery.unclassified",
      defaultMessage: "Unclassified",
    });
  }

  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export default function MediaGalleryPage() {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [filter, setFilter] = useState<SourceFilter>("all");
  const [pendingAssetId, setPendingAssetId] = useState<string | null>(null);
  const [deleteCandidateAssetId, setDeleteCandidateAssetId] = useState<string | null>(null);
  const [deletingAssetId, setDeletingAssetId] = useState<string | null>(null);
  const [uploading, setUploading] = useState<{ current: number; total: number } | null>(null);
  const filters = useMemo(
    () => [
      {
        value: "all" as const,
        label: intl.formatMessage({ id: "common.all", defaultMessage: "All" }),
      },
      {
        value: "upload" as const,
        label: intl.formatMessage({
          id: "mediaGallery.filter.uploaded",
          defaultMessage: "Uploaded",
        }),
      },
      {
        value: "generated" as const,
        label: intl.formatMessage({
          id: "mediaGallery.filter.generated",
          defaultMessage: "Generated",
        }),
      },
      {
        value: "website" as const,
        label: intl.formatMessage({ id: "mediaGallery.filter.website", defaultMessage: "Website" }),
      },
      {
        value: "instagram" as const,
        label: intl.formatMessage({ id: "common.instagram", defaultMessage: "Instagram" }),
      },
    ],
    [intl],
  );

  const { brand, brandQuery } = useRouteBrand();
  const sourceType = filter === "all" ? undefined : filter;
  const galleryQuery = trpc.mediaGallery.list.useQuery(
    { brandId: brand?.id ?? "", sourceType },
    {
      enabled: Boolean(brand?.id),
      refetchOnWindowFocus: true,
    },
  );
  const instagramConnectionQuery = trpc.socialConnection.getInstagram.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), refetchOnWindowFocus: true, staleTime: 60_000 },
  );
  trpc.mediaGallery.assetProcessingUpdates.useSubscription(
    { brandId: brand?.id ?? "" },
    {
      enabled: Boolean(brand?.id),
      onData: (event) => {
        utils.mediaGallery.list.setData({ brandId: brand?.id ?? "", sourceType }, (current) =>
          current?.map((asset) =>
            asset.canonicalAssetId === event.canonicalAssetId
              ? { ...asset, uploadProcessingStatus: event.status }
              : asset,
          ),
        );
        void utils.mediaGallery.list.invalidate();
        if (event.status !== "done" && event.status !== "failed") return;

        setTimeout(() => void utils.mediaGallery.list.invalidate(), 300);
        setTimeout(() => void utils.mediaGallery.list.invalidate(), 1_000);
      },
    },
  );
  const setReferenceEnabled = trpc.mediaGallery.setReferenceEnabled.useMutation({
    onSuccess: (_data, variables) => {
      utils.mediaGallery.list.setData({ brandId: variables.brandId, sourceType }, (current) =>
        current?.map((asset) =>
          asset.canonicalAssetId === variables.canonicalAssetId
            ? {
                ...asset,
                referenceEnabled: variables.enabled,
                realPeopleReferencePolicyApplies:
                  resolveMediaGalleryRealPeopleReferencePolicyApplies({
                    referenceEnabled: variables.enabled,
                    imageClassification: asset.imageClassification,
                    realPeopleDetectionStatus: asset.realPeopleDetectionStatus,
                  }),
              }
            : asset,
        ),
      );
    },
    onSettled: () => {
      setPendingAssetId(null);
    },
  });
  const setRealPeopleReferencePolicy = trpc.mediaGallery.setRealPeopleReferencePolicy.useMutation({
    onSuccess: (_data, variables) => {
      utils.mediaGallery.list.setData({ brandId: variables.brandId, sourceType }, (current) =>
        current?.map((asset) =>
          asset.canonicalAssetId === variables.canonicalAssetId
            ? { ...asset, realPeopleReferencePolicy: variables.policy }
            : asset,
        ),
      );
    },
    onSettled: () => {
      setPendingAssetId(null);
    },
  });
  const deleteAsset = trpc.mediaGallery.deleteAsset.useMutation({
    onSuccess: (_data, variables) => {
      utils.mediaGallery.list.setData({ brandId: variables.brandId, sourceType }, (current) =>
        current?.filter((asset) => asset.canonicalAssetId !== variables.canonicalAssetId),
      );
      void utils.mediaGallery.list.invalidate();
      toast.success(
        intl.formatMessage({
          id: "mediaGallery.deleteComplete",
          defaultMessage: "Deleted media asset.",
        }),
      );
    },
    onError: () => {
      toast.error(
        intl.formatMessage({
          id: "mediaGallery.deleteFailed",
          defaultMessage: "Could not delete this media asset. Try again.",
        }),
      );
    },
    onSettled: () => {
      setDeletingAssetId(null);
      setDeleteCandidateAssetId(null);
    },
  });
  const createUpload = trpc.mediaGallery.createUpload.useMutation();
  const completeUpload = trpc.mediaGallery.completeUpload.useMutation();

  const assets = useMemo(() => galleryQuery.data ?? [], [galleryQuery.data]);
  const refetchGallery = galleryQuery.refetch;
  const hasProcessingUploads = useMemo(
    () => assets.some((asset) => isMediaGalleryUploadProcessing(asset)),
    [assets],
  );
  const counts = useMemo(
    () => ({
      total: assets.length,
      selected: assets.filter((asset) => asset.referenceEnabled).length,
      uploaded: assets.filter((asset) => asset.sources.some((source) => source.type === "upload"))
        .length,
      generated: assets.filter((asset) =>
        asset.sources.some((source) => source.type === "generated"),
      ).length,
      website: assets.filter((asset) => asset.sources.some((source) => source.type === "website"))
        .length,
      instagram: assets.filter((asset) =>
        asset.sources.some((source) => source.type === "instagram"),
      ).length,
    }),
    [assets],
  );
  const uploadBusy = Boolean(uploading) || createUpload.isPending || completeUpload.isPending;
  const showInstagramImportBanner =
    instagramConnectionQuery.data !== undefined &&
    instagramConnectionQuery.data?.status !== "connected";

  useEffect(() => {
    if (!hasProcessingUploads) return;

    const intervalId = window.setInterval(() => {
      void refetchGallery();
    }, 1_500);

    return () => window.clearInterval(intervalId);
  }, [hasProcessingUploads, refetchGallery]);

  const uploadFile = async (file: File) => {
    if (!brand?.id) return;

    const upload = await createUpload.mutateAsync({
      brandId: brand.id,
      fileName: file.name,
      contentType: file.type as MediaUploadContentType,
      sizeBytes: file.size,
    });
    const response = await fetch(upload.uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": upload.contentType },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Media upload failed with status ${response.status}`);
    }

    await completeUpload.mutateAsync({
      brandId: brand.id,
      objectKey: upload.objectKey,
      fileName: file.name,
      contentType: upload.contentType,
      sizeBytes: file.size,
    });
  };

  const handleFilesSelected = async (fileList: FileList | null) => {
    if (!brand?.id || !fileList || fileList.length === 0) return;

    const files = Array.from(fileList);
    const validFiles: File[] = [];
    for (const file of files) {
      if (!MEDIA_UPLOAD_CONTENT_TYPES.includes(file.type as MediaUploadContentType)) {
        toast.error(
          intl.formatMessage({
            id: "mediaGallery.uploadUnsupported",
            defaultMessage: "Upload PNG, JPG, WebP, or SVG images.",
          }),
        );
        continue;
      }

      if (file.size > MAX_MEDIA_UPLOAD_BYTES) {
        toast.error(
          intl.formatMessage(
            {
              id: "mediaGallery.uploadTooLarge",
              defaultMessage: "Image files must be {size} MB or smaller.",
            },
            { size: MAX_MEDIA_UPLOAD_BYTES / (1024 * 1024) },
          ),
        );
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      if (uploadInputRef.current) uploadInputRef.current.value = "";
      return;
    }

    try {
      for (const [index, file] of validFiles.entries()) {
        setUploading({ current: index + 1, total: validFiles.length });
        await uploadFile(file);
      }
      await utils.mediaGallery.list.invalidate();
      toast.success(
        intl.formatMessage(
          {
            id: "mediaGallery.uploadComplete",
            defaultMessage: "Uploaded {count, plural, one {# image} other {# images}}.",
          },
          { count: validFiles.length },
        ),
      );
    } catch {
      toast.error(
        intl.formatMessage({
          id: "mediaGallery.uploadFailed",
          defaultMessage: "Could not upload every image. Try again.",
        }),
      );
    } finally {
      setUploading(null);
      if (uploadInputRef.current) uploadInputRef.current.value = "";
    }
  };

  const handleDeleteAsset = (canonicalAssetId: string) => {
    if (!brand?.id || deleteAsset.isPending) return;

    setDeleteCandidateAssetId(canonicalAssetId);
  };

  const confirmDeleteAsset = () => {
    if (!brand?.id || !deleteCandidateAssetId || deleteAsset.isPending) return;

    setDeletingAssetId(deleteCandidateAssetId);
    deleteAsset.mutate({ brandId: brand.id, canonicalAssetId: deleteCandidateAssetId });
  };

  if (brandQuery.isPending) {
    return (
      <CenteredLoader
        label={intl.formatMessage({
          id: "mediaGallery.loading",
          defaultMessage: "Loading brand assets…",
        })}
      />
    );
  }

  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 px-4 py-5 sm:px-6">
      <PageIntro
        title={<FormattedMessage id="mediaGallery.title" defaultMessage="Media Gallery" />}
      />

      {showInstagramImportBanner ? (
        <div className="flex flex-col gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm shadow-xs sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3 sm:items-center">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-border bg-muted/30 shadow-sm">
              <img src="/instagram.svg" alt="" className="h-5 w-5" aria-hidden={true} />
            </span>
            <div className="min-w-0 space-y-1">
              <p className="font-semibold text-foreground">
                <FormattedMessage
                  id="mediaGallery.instagramImportBanner.title"
                  defaultMessage="Connect Instagram to fill your media library."
                />
              </p>
              <p className="text-sm text-muted-foreground">
                <FormattedMessage
                  id="mediaGallery.instagramImportBanner.description"
                  defaultMessage="Cheetah can fetch your Instagram posts and make them available as brand references for future content."
                />
              </p>
            </div>
          </div>
          <Button
            asChild
            variant="outline"
            size="sm"
            className="h-8 shrink-0 rounded-lg px-4 text-xs font-extrabold shadow-sm"
          >
            <Link to={buildBrandPath(brand.id, "/integrations/instagram")}>
              <FormattedMessage
                id="mediaGallery.instagramImportBanner.connect"
                defaultMessage="Connect Instagram"
              />
            </Link>
          </Button>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedFilter options={filters} value={filter} onChange={setFilter} />
        <div className="flex flex-wrap items-center justify-end gap-3">
          <p className="text-sm text-muted-foreground">
            <FormattedMessage
              id="mediaGallery.compactSummary"
              defaultMessage="{total} assets · {selected} selected · {uploaded} uploaded · {generated} generated · {instagram} from Instagram"
              values={counts}
            />
          </p>
          <input
            ref={uploadInputRef}
            type="file"
            accept={MEDIA_UPLOAD_ACCEPT}
            multiple
            className="hidden"
            onChange={(event) => {
              void handleFilesSelected(event.currentTarget.files);
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={uploadBusy}
            onClick={() => uploadInputRef.current?.click()}
          >
            <Upload className="h-3.5 w-3.5" aria-hidden="true" />
            {uploading ? (
              <FormattedMessage
                id="mediaGallery.uploadingProgress"
                defaultMessage="Uploading {current}/{total}"
                values={uploading}
              />
            ) : (
              <FormattedMessage id="mediaGallery.uploadImages" defaultMessage="Upload images" />
            )}
          </Button>
        </div>
      </div>

      {galleryQuery.isPending ? (
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          aria-hidden="true"
        >
          {Array.from({ length: 8 }).map((_, index) => (
            <Card key={index} className="overflow-hidden rounded-3xl shadow-xs">
              <Skeleton className="aspect-[4/3] rounded-none" />
              <div className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-3 w-12" />
                </div>
                <div className="flex gap-1.5">
                  <Skeleton className="h-6 w-20 rounded-full" />
                  <Skeleton className="h-6 w-24 rounded-full" />
                </div>
                <Skeleton className="h-9 w-full" />
              </div>
            </Card>
          ))}
        </div>
      ) : galleryQuery.error ? (
        <Card>
          <CardContent className="flex h-40 items-center justify-center text-sm text-destructive">
            <FormattedMessage
              id="mediaGallery.loadFailed"
              defaultMessage="Failed to load media gallery: {message}"
              values={{ message: galleryQuery.error.message }}
            />
          </CardContent>
        </Card>
      ) : assets.length === 0 ? (
        <EmptyCard icon={<Images className="h-8 w-8" />} contentClassName="h-56">
          <FormattedMessage
            id="mediaGallery.empty"
            defaultMessage="No media assets are available for this brand yet."
          />
        </EmptyCard>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {assets.map((asset) => {
            const instagramSource = asset.sources.find((source) => source.type === "instagram");
            const websiteSource = asset.sources.find((source) => source.type === "website");
            const uploadSource = asset.sources.find((source) => source.type === "upload");
            const generatedSource = asset.sources.find((source) => source.type === "generated");
            const sourceUrl = instagramSource?.instagramPermalink ?? websiteSource?.pageUrl ?? null;
            const sourceLinkLabel = instagramSource
              ? getMediaGallerySourceLinkLabel("instagram", intl)
              : getMediaGallerySourceLinkLabel("website", intl);
            const isPending = pendingAssetId === asset.canonicalAssetId;
            const peopleConfidence = formatPeopleDetectionConfidence(
              asset.containsRealPeopleConfidencePct,
            );
            const showPendingPeopleCheck =
              asset.realPeopleReferencePolicyApplies &&
              (asset.realPeopleDetectionStatus === "pending" ||
                asset.realPeopleDetectionStatus === "unknown");
            const allowPeopleFromReference = asset.realPeopleReferencePolicy === "allow_people";
            const peoplePolicyTooltipId = `people-policy-help-${asset.id}`;
            const peoplePolicyToggleId = `people-policy-toggle-${asset.id}`;
            const uploadProcessing = isMediaGalleryUploadProcessing(asset);
            const uploadProcessingFailed = asset.uploadProcessingStatus === "failed";
            const isDeletePending = deletingAssetId === asset.canonicalAssetId;

            return (
              <Card
                key={asset.id}
                className={cn(
                  "group flex h-full flex-col gap-0 rounded-3xl p-0 shadow-xs transition-[border-color,box-shadow,ring-color] hover:shadow-sm",
                  asset.referenceEnabled && "border-primary/25 ring-1 ring-primary/15",
                )}
              >
                <div className="relative aspect-[4/3] overflow-hidden rounded-t-[calc(1.5rem-1px)] bg-muted">
                  {asset.assetKind === "image" ? (
                    <img
                      src={asset.signedUrl}
                      alt={instagramSource?.instagramCaption ?? asset.objectKey}
                      className="h-full w-full rounded-t-[calc(1.5rem-1px)] object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center rounded-t-[calc(1.5rem-1px)] text-muted-foreground">
                      <ImageIcon className="h-8 w-8" />
                    </div>
                  )}
                  <div className="absolute inset-x-0 top-0 flex flex-wrap items-start gap-1.5 bg-gradient-to-b from-black/45 to-transparent p-3">
                    {asset.referenceEnabled ? (
                      <Badge
                        shape="pill"
                        className="gap-1 bg-primary/90 text-primary-foreground backdrop-blur"
                      >
                        <Sparkles className="h-3 w-3" aria-hidden="true" />
                        <FormattedMessage id="mediaGallery.reference" defaultMessage="Reference" />
                      </Badge>
                    ) : null}
                    {instagramSource ? (
                      <Badge
                        shape="pill"
                        className="gap-1 bg-background/90 text-foreground backdrop-blur"
                      >
                        <Images className="h-3 w-3" aria-hidden="true" />
                        <FormattedMessage id="common.instagram" defaultMessage="Instagram" />
                      </Badge>
                    ) : generatedSource ? (
                      <Badge
                        shape="pill"
                        className="gap-1 bg-background/90 text-foreground backdrop-blur"
                      >
                        <Sparkles className="h-3 w-3" aria-hidden="true" />
                        <FormattedMessage
                          id="mediaGallery.source.generated"
                          defaultMessage="Generated"
                        />
                      </Badge>
                    ) : uploadSource ? (
                      <Badge
                        shape="pill"
                        className="gap-1 bg-background/90 text-foreground backdrop-blur"
                      >
                        <Upload className="h-3 w-3" aria-hidden="true" />
                        <FormattedMessage
                          id="mediaGallery.source.uploaded"
                          defaultMessage="Uploaded"
                        />
                      </Badge>
                    ) : websiteSource ? (
                      <Badge
                        shape="pill"
                        className="bg-background/90 text-foreground backdrop-blur"
                      >
                        <FormattedMessage
                          id="mediaGallery.source.website"
                          defaultMessage="Website"
                        />
                      </Badge>
                    ) : null}
                    {asset.containsRealPeople ? (
                      <Badge
                        shape="pill"
                        className="gap-1 bg-background/90 text-foreground backdrop-blur"
                      >
                        <Users className="h-3 w-3" aria-hidden="true" />
                        {peopleConfidence ? (
                          <FormattedMessage
                            id="mediaGallery.peopleDetectedWithConfidence"
                            defaultMessage="People detected · {confidence}"
                            values={{ confidence: peopleConfidence }}
                          />
                        ) : (
                          <FormattedMessage
                            id="mediaGallery.peopleDetected"
                            defaultMessage="People detected"
                          />
                        )}
                      </Badge>
                    ) : null}
                    {showPendingPeopleCheck ? (
                      <Badge
                        shape="pill"
                        className="gap-1 bg-background/90 text-foreground backdrop-blur"
                      >
                        <Users className="h-3 w-3" aria-hidden="true" />
                        <FormattedMessage
                          id="mediaGallery.peopleCheckPending"
                          defaultMessage="People check pending"
                        />
                      </Badge>
                    ) : null}
                    {uploadProcessing ? (
                      <Badge
                        shape="pill"
                        className="gap-1 bg-background/90 text-foreground backdrop-blur"
                      >
                        <Sparkles className="h-3 w-3 animate-pulse" aria-hidden="true" />
                        <FormattedMessage
                          id="mediaGallery.processing"
                          defaultMessage="Processing"
                        />
                      </Badge>
                    ) : uploadProcessingFailed ? (
                      <Badge
                        shape="pill"
                        className="gap-1 bg-destructive/90 text-destructive-foreground backdrop-blur"
                      >
                        <Sparkles className="h-3 w-3" aria-hidden="true" />
                        <FormattedMessage
                          id="mediaGallery.processingFailed"
                          defaultMessage="Processing failed"
                        />
                      </Badge>
                    ) : null}
                  </div>
                  <div
                    className={cn(
                      "pointer-events-none absolute inset-0 rounded-t-[calc(1.5rem-1px)] opacity-0 ring-1 ring-inset ring-primary/30 transition-opacity",
                      asset.referenceEnabled && "opacity-100",
                    )}
                    aria-hidden="true"
                  />
                </div>
                <CardHeader className="space-y-1 p-4 pb-2">
                  <CardTitle className="line-clamp-1 text-sm font-semibold">
                    {asset.displayName ?? formatClassification(asset.imageClassification, intl)}
                  </CardTitle>
                  <p className="truncate font-mono text-xs text-muted-foreground">
                    {asset.width && asset.height
                      ? `${asset.width} x ${asset.height}px`
                      : intl.formatMessage({
                          id: "mediaGallery.noDimensions",
                          defaultMessage: "No dimensions",
                        })}{" "}
                    {" · "}
                    {formatBytes(asset.sizeBytes, intl)}
                  </p>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col gap-3 p-4 pt-1">
                  {instagramSource?.instagramCaption ? (
                    <p className="line-clamp-2 text-xs leading-5 text-muted-foreground">
                      {instagramSource.instagramCaption}
                    </p>
                  ) : null}
                  {asset.realPeopleReferencePolicyApplies ? (
                    <div className="mt-2 flex items-center justify-between gap-3 border-t pt-4">
                      <div className="flex min-w-0 items-center gap-2">
                        <label
                          htmlFor={peoplePolicyToggleId}
                          className="cursor-pointer truncate text-xs font-medium text-foreground"
                        >
                          {getRealPeopleReferencePolicyLabel("allow_people", intl)}
                        </label>
                        <span className="group/people-policy relative inline-flex">
                          <button
                            type="button"
                            aria-describedby={peoplePolicyTooltipId}
                            aria-label={intl.formatMessage({
                              id: "mediaGallery.peoplePolicyTooltipLabel",
                              defaultMessage: "Real people reference policy details",
                            })}
                            className="rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                          >
                            <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                          <span
                            id={peoplePolicyTooltipId}
                            role="tooltip"
                            className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden w-64 -translate-x-1/2 rounded-xl border bg-popover px-3 py-2 text-left text-xs leading-5 text-popover-foreground shadow-lg group-focus-within/people-policy:block group-hover/people-policy:block"
                          >
                            {asset.containsRealPeople ? (
                              <FormattedMessage
                                id="mediaGallery.peoplePolicyHelp"
                                defaultMessage="This reference includes real people. Choose whether generated images can include people from this reference, or only borrow its style and setting."
                              />
                            ) : (
                              <FormattedMessage
                                id="mediaGallery.peoplePolicyPendingHelp"
                                defaultMessage="This selected photo is still being checked for people. Choose how Cheetah should treat it if people are present."
                              />
                            )}
                          </span>
                        </span>
                      </div>
                      <label
                        htmlFor={peoplePolicyToggleId}
                        className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border border-border bg-muted px-0.5 transition-colors hover:border-primary/30 has-disabled:cursor-not-allowed has-disabled:opacity-60 has-checked:border-primary has-checked:bg-primary has-focus-visible:ring-2 has-focus-visible:ring-ring/50"
                      >
                        <input
                          id={peoplePolicyToggleId}
                          type="checkbox"
                          className="peer sr-only"
                          checked={allowPeopleFromReference}
                          disabled={isPending || setRealPeopleReferencePolicy.isPending}
                          onChange={(event) => {
                            setPendingAssetId(asset.canonicalAssetId);
                            setRealPeopleReferencePolicy.mutate({
                              brandId: brand.id,
                              canonicalAssetId: asset.canonicalAssetId,
                              policy: event.currentTarget.checked
                                ? "allow_people"
                                : "style_and_setting_only",
                            });
                          }}
                        />
                        <span
                          className="h-5 w-5 rounded-full bg-background shadow-sm transition-transform peer-checked:translate-x-5"
                          aria-hidden="true"
                        />
                      </label>
                    </div>
                  ) : null}
                  <div className="mt-auto space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <Button
                        type="button"
                        size="icon-sm"
                        variant="ghost"
                        className="text-muted-foreground/70 hover:bg-destructive/10 hover:text-destructive"
                        aria-label={intl.formatMessage(
                          {
                            id: "mediaGallery.deleteAssetLabel",
                            defaultMessage: "Delete {name}",
                          },
                          {
                            name:
                              asset.displayName ??
                              formatClassification(asset.imageClassification, intl),
                          },
                        )}
                        disabled={
                          isPending ||
                          deleteAsset.isPending ||
                          setReferenceEnabled.isPending ||
                          setRealPeopleReferencePolicy.isPending
                        }
                        onClick={() => handleDeleteAsset(asset.canonicalAssetId)}
                      >
                        {isDeletePending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        )}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={asset.referenceEnabled ? "outline" : "default"}
                        className="gap-1.5"
                        disabled={
                          isPending ||
                          deleteAsset.isPending ||
                          setReferenceEnabled.isPending ||
                          setRealPeopleReferencePolicy.isPending
                        }
                        onClick={() => {
                          setPendingAssetId(asset.canonicalAssetId);
                          setReferenceEnabled.mutate({
                            brandId: brand.id,
                            canonicalAssetId: asset.canonicalAssetId,
                            enabled: !asset.referenceEnabled,
                          });
                        }}
                      >
                        {isPending ? (
                          intl.formatMessage({ id: "common.saving", defaultMessage: "Saving…" })
                        ) : asset.referenceEnabled ? (
                          <>
                            <Check className="h-3.5 w-3.5" aria-hidden="true" />
                            <FormattedMessage
                              id="mediaGallery.selected"
                              defaultMessage="Selected"
                            />
                          </>
                        ) : (
                          intl.formatMessage({
                            id: "mediaGallery.useAsReference",
                            defaultMessage: "Use as reference",
                          })
                        )}
                      </Button>
                    </div>
                    {sourceUrl ? (
                      <div className="flex justify-center pt-1">
                        <ExternalLinkText
                          href={sourceUrl}
                          className="text-[11px] font-normal opacity-75"
                          truncate
                        >
                          {sourceLinkLabel}
                        </ExternalLinkText>
                      </div>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {deleteCandidateAssetId ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-background/75 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="media-gallery-delete-title"
        >
          <Card className="w-full max-w-md rounded-3xl border-border/70 shadow-xl">
            <CardContent className="space-y-5 p-6">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
                  <Trash2 className="h-5 w-5" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h2
                    id="media-gallery-delete-title"
                    className="text-lg font-semibold tracking-tight text-foreground"
                  >
                    <FormattedMessage
                      id="mediaGallery.deleteConfirmTitle"
                      defaultMessage="Delete image?"
                    />
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    <FormattedMessage
                      id="mediaGallery.deleteConfirm"
                      defaultMessage="This image will be deleted."
                    />
                  </p>
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDeleteCandidateAssetId(null)}
                  disabled={deleteAsset.isPending}
                >
                  <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={confirmDeleteAsset}
                  disabled={deleteAsset.isPending}
                >
                  {deleteAsset.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  {deleteAsset.isPending ? (
                    <FormattedMessage id="common.deleting" defaultMessage="Deleting..." />
                  ) : (
                    <FormattedMessage id="common.delete" defaultMessage="Delete" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
